import { Tooltipß } from '@otter-ai/den-components';
import { InformationIcon } from '@otter-ai/den-icons';
import { useTranslations } from 'next-intl';
import { AutoRenewSkeleton, PriceSkeleton } from './PriceSkeleton';

interface PricingSummaryProps {
  sessionIsUpdating: boolean;
  total: {
    subtotal: { amount: string };
    taxExclusive: { amount: string; minorUnitsAmount: number };
    total: { amount: string };
    appliedBalance: { amount: string; minorUnitsAmount: number };
  };
  recurring?: {
    dueNext: { subtotal: { amount: string } };
  } | null;
  renewalDate?: string | null;
  minorUnitsAmountDivisor: number;
}

export function PricingSummary({
  sessionIsUpdating,
  total,
  recurring,
  renewalDate,
  minorUnitsAmountDivisor,
}: PricingSummaryProps) {
  const t = useTranslations('settings:billing');

  return (
    <div className="flex flex-col gap-4">
      {total.taxExclusive.minorUnitsAmount > 0 && (
        <div className="flex justify-between text-sm text-palette-gray-950 font-regular">
          <div className="flex items-center">
            <span>{t('checkout.estimatedSalesTax')}</span>
            <Tooltipß
              delayDuration={0}
              caption={t('checkout.salesTaxExplanation')}
              side="top"
            >
              <span>
                <InformationIcon color="#5D7284" />
              </span>
            </Tooltipß>
          </div>
          <div className="max-h-4">
            <PriceSkeleton sessionIsUpdating={sessionIsUpdating}>
              {total.taxExclusive.amount}
            </PriceSkeleton>
          </div>
        </div>
      )}

      <div className="bg-palette-pink-250 h-[1px] w-full" />

      {total.appliedBalance.minorUnitsAmount ? (
        <div className="flex justify-between text-palette-gray-950 font-regular">
          <div className="flex items-center">
            <span>{t('checkout.creditsApplied')}</span>
            <Tooltipß
              delayDuration={0}
              caption={t('checkout.creditsAppliedExplanation', {
                credits: formatCredit(
                  -total.appliedBalance.minorUnitsAmount,
                  minorUnitsAmountDivisor,
                ),
              })}
              side="top"
            >
              <span>
                <InformationIcon color="#5D7284" />
              </span>
            </Tooltipß>
          </div>
          <span>{`${total.appliedBalance.amount}`}</span>
        </div>
      ) : null}

      <div className="flex justify-between text-lg font-semibold items-center">
        {t('checkout.dueToday')}
        <div className="font-semibold max-h-5 flex items-center">
          <PriceSkeleton
            sessionIsUpdating={sessionIsUpdating}
            className="h-5 my-0"
          >
            {`${t('checkout.US')}${total.total.amount}`}
          </PriceSkeleton>
        </div>
      </div>

      <div className="text-sm text-palette-gray-600 font-regular max-h-4 flex justify-between w-full items-center">
        <AutoRenewSkeleton sessionIsUpdating={sessionIsUpdating}>
          {renewalDate && recurring
            ? t('checkout.autoRenewInfo', {
                renewal_date: renewalDate,
                renewal_price: recurring?.dueNext.subtotal.amount,
              })
            : null}
        </AutoRenewSkeleton>
        <span className="text-sm text-palette-gray-600 font-regular">
          {t('checkout.USD')}
        </span>
      </div>
    </div>
  );
}

function formatCredit(credit: number, minorUnitsAmountDivisor: number) {
  const amount = credit / minorUnitsAmountDivisor;
  const absAmount = Math.abs(amount);
  const formattedAmount = absAmount.toFixed(2);
  return amount < 0 ? `-$${formattedAmount}` : `$${formattedAmount}`;
}
